import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  AD_BANNER_ASPECT_WIDTH_OVER_HEIGHT,
  normalizeAdSlideHref,
  type AdSlide,
} from '../../lib/siteAdvertisingConfig';

export interface AdCarouselProps {
  slides: AdSlide[];
  maxHeightPx: number;
  autoplaySeconds: number;
  showDots: boolean;
  showArrows: boolean;
  className?: string;
  'aria-label'?: string;
}

export function AdCarousel({
  slides,
  maxHeightPx,
  autoplaySeconds,
  showDots,
  showArrows,
  className = '',
  'aria-label': ariaLabel = 'Advertisement carousel',
}: AdCarouselProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  const count = slides.length;
  const go = useCallback(
    (delta: number) => {
      if (count === 0) return;
      setIndex((i) => (i + delta + count) % count);
    },
    [count]
  );

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || count === 0) return;
    const w = el.clientWidth;
    el.scrollTo({ left: index * w, behavior: 'smooth' });
  }, [index, count]);

  useEffect(() => {
    if (autoplaySeconds <= 0 || count <= 1) return;
    const t = window.setInterval(() => go(1), autoplaySeconds * 1000);
    return () => window.clearInterval(t);
  }, [autoplaySeconds, count, go]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || count === 0) return;

    const onScrollEnd = () => {
      const w = el.clientWidth;
      if (w <= 0) return;
      const i = Math.round(el.scrollLeft / w);
      setIndex(Math.max(0, Math.min(count - 1, i)));
    };

    let scrollTimer: number;
    const onScroll = () => {
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(onScrollEnd, 80);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.clearTimeout(scrollTimer);
      el.removeEventListener('scroll', onScroll);
    };
  }, [count]);

  if (count === 0) return null;

  const renderSlide = (slide: AdSlide, i: number) => {
    const frameHeight = Math.max(1, maxHeightPx);
    const frameMaxWidthPx = frameHeight * AD_BANNER_ASPECT_WIDTH_OVER_HEIGHT;
    const img = (
      <img
        src={slide.imageUrl}
        alt={slide.alt || 'Advertisement'}
        className="max-h-full max-w-full h-auto w-auto object-contain select-none pointer-events-none"
        loading={i === 0 ? 'eager' : 'lazy'}
        draggable={false}
      />
    );

    const inner = (
      <div className="flex w-full min-w-0 justify-center px-2 py-1">
        <div
          className="flex w-full max-w-full items-center justify-center overflow-hidden rounded bg-gray-100 dark:bg-slate-800/80"
          style={{
            width: `min(100%, ${frameMaxWidthPx}px)`,
            aspectRatio: `${AD_BANNER_ASPECT_WIDTH_OVER_HEIGHT} / 1`,
            maxHeight: frameHeight,
          }}
        >
          {img}
        </div>
      </div>
    );

    const resolvedHref = slide.href ? normalizeAdSlideHref(slide.href) : undefined;
    if (resolvedHref) {
      const openInNewTab = /^https?:\/\//i.test(resolvedHref);
      return (
        <a
          key={`${slide.imageUrl}-${i}`}
          href={resolvedHref}
          className="block min-w-full flex-shrink-0 snap-center snap-always focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 rounded"
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          target={openInNewTab ? '_blank' : undefined}
          aria-label={slide.alt || 'Advertisement link'}
        >
          {inner}
        </a>
      );
    }

    return (
      <div key={`${slide.imageUrl}-${i}`} className="min-w-full flex-shrink-0 snap-center snap-always">
        {inner}
      </div>
    );
  };

  return (
    <div
      className={`relative w-full max-w-5xl mx-auto p-4 ${className}`}
      role="region"
      aria-label={ariaLabel}
    >
      {showArrows && count > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(-1)}
            className="absolute left-1 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-white/90 dark:bg-slate-900/90 text-gray-800 dark:text-white shadow border border-gray-200 dark:border-slate-600 hover:bg-white dark:hover:bg-slate-800"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            className="absolute right-1 top-1/2 -translate-y-1/2 z-10 p-1.5 rounded-full bg-white/90 dark:bg-slate-900/90 text-gray-800 dark:text-white shadow border border-gray-200 dark:border-slate-600 hover:bg-white dark:hover:bg-slate-800"
            aria-label="Next slide"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}
      <div
        ref={scrollerRef}
        className="flex overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {slides.map((s, i) => renderSlide(s, i))}
      </div>
      {showDots && count > 1 && (
        <div className="flex justify-center gap-1.5 py-2" role="tablist" aria-label="Carousel slides">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === index}
              className={`h-2 w-2 rounded-full transition-colors ${
                i === index ? 'bg-teal-600 dark:bg-teal-400' : 'bg-gray-300 dark:bg-slate-600'
              }`}
              onClick={() => setIndex(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
